import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'

// Store initialized lightboxes to prevent double initialization
const initializedGalleries = new Set()

/**
 * Initialize PhotoSwipe Lightbox with custom thumbnail navigation
 * @param {string} gallerySelector - CSS selector for the gallery container
 * @param {string} childrenSelector - CSS selector for gallery items (default: 'a')
 */
export default function initPhotoSwipeLightbox(gallerySelector, childrenSelector = 'a') {
  const gallery = document.querySelector(gallerySelector)
  if (!gallery) return null
  
  // Prevent double initialization
  if (initializedGalleries.has(gallerySelector)) {
    return null
  }
  
  initializedGalleries.add(gallerySelector)

  const lightbox = new PhotoSwipeLightbox({
    gallery: gallerySelector,
    children: childrenSelector,
    pswpModule: () => import('photoswipe'),
    
    // Configuration
    showHideAnimationType: 'zoom',
    bgOpacity: 0.9,
    spacing: 0.1,
    allowPanToNext: true,
    loop: false,
    
    // Click actions
    imageClickAction: 'zoom-or-close',
    bgClickAction: 'close',
    tapAction: 'toggle-controls',
    doubleTapAction: 'zoom',
    
    // Keyboard
    escKey: true,
    arrowKeys: true,
    
    // Preload
    preload: [1, 2],
    
    // Custom class for styling
    mainClass: 'pswp--lsa-custom',
    
    // Zoom configuration using official PhotoSwipe API
    // See: https://photoswipe.com/adjusting-zoom-level/
    initialZoomLevel: 'fit',
    
    // Secondary zoom level - set to 2.5x for deep zoom beyond screen
    // PhotoSwipe will toggle between initialZoomLevel (1) and secondaryZoomLevel (2.5)
    // Desktop: clicking will toggle 1x ↔ 2.5x
    secondaryZoomLevel: (zoomLevelObject) => {
      const isMobile = window.innerWidth <= 768
      if (isMobile) {
        // Mobile: use fit-based calculation, capped at 2.5x
        return Math.min(zoomLevelObject.fit * 2.5, 2.5)
      }
      // Desktop: zoom to 2.5x (beyond screen - requires scrolling)
      return 1
    },
    
    // Maximum zoom level for pinch/gesture zoom
    maxZoomLevel: 4
  })
  
  // Handle zoom completion to update CSS classes for styling
  lightbox.on('afterInit', () => {
    const pswp = lightbox.pswp
    if (!pswp) return
    
    // Update CSS classes based on zoom level for styling purposes
    pswp.on('zoomComplete', () => {
      const pswpElement = pswp.element
      if (!pswpElement) return
      
      const currentZoom = pswp.currZoomLevel || 1
      
      // Remove all zoom classes
      pswpElement.classList.remove('photoswipe--zoom-50', 'photoswipe--zoom-100', 'photoswipe--zoom-250')
      
      // Add appropriate class based on zoom level
      if (currentZoom < 1.5) {
        pswpElement.classList.add('photoswipe--zoom-100')
      } else if (currentZoom >= 2) {
        pswpElement.classList.add('photoswipe--zoom-250')
      }
    })
    
    // Remove zoom classes when changing slides
    pswp.on('change', () => {
      if (pswp.element) {
        pswp.element.classList.remove('photoswipe--zoom-50', 'photoswipe--zoom-100', 'photoswipe--zoom-250')
      }
    })
  })

  // Register custom thumbnail UI on the left side
  lightbox.on('uiRegister', function() {
    lightbox.pswp.ui.registerElement({
      name: 'thumbnails',
      className: 'pswp__thumbnails',
      appendTo: 'wrapper',
      order: 1,
      onInit: (el, pswp) => {
        const thumbnailsContainer = document.createElement('div')
        thumbnailsContainer.className = 'pswp__thumbnails-container'
        
        const thumbnailsList = document.createElement('div')
        thumbnailsList.className = 'pswp__thumbnails-list'
        
        // Get all gallery items (links with PhotoSwipe data attributes)
        const galleryItems = gallery.querySelectorAll(childrenSelector)
        
        // Filter to only image items (those with data-pswp-width attribute)
        const imageItems = Array.from(galleryItems).filter(item => {
          return item.hasAttribute('data-pswp-width') && item.hasAttribute('data-pswp-height')
        })
        
        // Create thumbnail for each image
        imageItems.forEach((item, index) => {
          const thumbnail = document.createElement('button')
          thumbnail.className = 'pswp__thumbnail'
          thumbnail.setAttribute('aria-label', `Go to image ${index + 1}`)
          thumbnail.type = 'button'
          
          // Get thumbnail image from the link's img child or from srcset
          let thumbnailSrc = null
          const img = item.querySelector('img')
          if (img) {
            // Use the img src, or try srcset if available
            thumbnailSrc = img.currentSrc || img.src
          }
          
          // Fallback: try to extract from href if it's an image URL
          if (!thumbnailSrc) {
            const href = item.getAttribute('href') || item.getAttribute('data-pswp-src')
            if (href && href.match(/\.(jpg|jpeg|png|gif|webp)/i)) {
              thumbnailSrc = href
            }
          }
          
          if (thumbnailSrc) {
            const thumbnailImg = document.createElement('img')
            thumbnailImg.src = thumbnailSrc
            thumbnailImg.alt = item.getAttribute('aria-label') || item.querySelector('img')?.alt || `Image ${index + 1}`
            thumbnailImg.className = 'pswp__thumbnail-img'
            thumbnail.appendChild(thumbnailImg)
          } else {
            // Create placeholder if no image found
            thumbnail.textContent = index + 1
            thumbnail.style.display = 'flex'
            thumbnail.style.alignItems = 'center'
            thumbnail.style.justifyContent = 'center'
            thumbnail.style.fontSize = '14px'
            thumbnail.style.color = '#666'
          }
          
          thumbnail.onclick = (e) => {
            e.preventDefault()
            e.stopPropagation()
            pswp.goTo(index)
          }
          
          thumbnailsList.appendChild(thumbnail)
        })
        
        thumbnailsContainer.appendChild(thumbnailsList)
        el.appendChild(thumbnailsContainer)

        // Prevent PhotoSwipe and page from intercepting wheel events over the thumbnails container
        thumbnailsContainer.addEventListener('wheel', (e) => {
          e.stopPropagation()
          e.preventDefault()
          thumbnailsContainer.scrollTop += e.deltaY
        }, { passive: false })
        
        // Update active thumbnail on slide change
        let prevIndex = -1
        const thumbnails = thumbnailsList.querySelectorAll('.pswp__thumbnail')
        
        pswp.on('change', () => {
          if (prevIndex >= 0 && thumbnails[prevIndex]) {
            thumbnails[prevIndex].classList.remove('pswp__thumbnail--active')
          }
          if (thumbnails[pswp.currIndex]) {
            thumbnails[pswp.currIndex].classList.add('pswp__thumbnail--active')
            // Scroll thumbnail into view (works for both vertical desktop and horizontal mobile)
            thumbnails[pswp.currIndex].scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            })
          }
          prevIndex = pswp.currIndex
        })
        
        // Set initial active thumbnail
        if (thumbnails[0]) {
          thumbnails[0].classList.add('pswp__thumbnail--active')
        }
      }
    })
  })

  // Lock body scroll when lightbox opens, restore on close
  lightbox.on('open', () => {
    document.body.style.overflow = 'hidden'
  })

  lightbox.on('close', () => {
    document.body.style.overflow = ''
  })

  lightbox.init()
  
  return lightbox
}

/**
 * Initialize all PhotoSwipe galleries on the page
 */
export function initAllPhotoSwipeGalleries() {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeGalleries()
    })
  } else {
    initializeGalleries()
  }
  
  function initializeGalleries() {
    // Use MutationObserver to handle dynamically added galleries
    const observer = new MutationObserver((mutations) => {
      document.querySelectorAll('.pswp-gallery').forEach((gallery) => {
        const galleryId = gallery.id || gallery.getAttribute('data-gallery-id')
        if (galleryId && !initializedGalleries.has(`#${galleryId}`)) {
          initPhotoSwipeLightbox(`#${galleryId}`, 'a')
        }
      })
    })
    
    // Initial scan
    document.querySelectorAll('.pswp-gallery').forEach((gallery) => {
      const galleryId = gallery.id || gallery.getAttribute('data-gallery-id')
      if (galleryId) {
        initPhotoSwipeLightbox(`#${galleryId}`, 'a')
      } else {
        // Fallback: use a unique selector
        const tempId = 'pswp-gallery-' + Math.random().toString(36).substr(2, 9)
        gallery.id = tempId
        initPhotoSwipeLightbox(`#${tempId}`, 'a')
      }
    })
    
    // Observe for new galleries
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  }
}
